import type { MouseEvent } from 'react';

import type { BaseBreadcrumbItem, BaseBreadcrumbProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn } from './utils';

function isCurrentItem(item: BaseBreadcrumbItem, index: number, length: number) {
  return item.current ?? index === length - 1;
}

export function BaseBreadcrumb({
  items = [],
  separator = '/',
  appearance,
  className,
  classNames,
  style,
  onSelect,
  ...breadcrumbProps
}: BaseBreadcrumbProps) {
  const theme = resolveBaseAppearance(appearance);

  const handleClick = (
    event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    item: BaseBreadcrumbItem,
    index: number,
  ) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    onSelect?.(item, index);
  };

  return (
    <nav
      {...breadcrumbProps}
      aria-label={breadcrumbProps['aria-label'] ?? 'Breadcrumb'}
      className={cn(theme.slots.breadcrumbRoot, classNames?.root, className)}
      style={{
        ...theme.styles.breadcrumb,
        ...style,
      }}
    >
      <ol className={cn(theme.slots.breadcrumbList, classNames?.list)}>
        {items.map((item, index) => {
          const current = isCurrentItem(item, index, items.length);
          return (
            <li
              key={`${index}-${item.value ?? item.href ?? String(item.label)}`}
              className={cn(theme.slots.breadcrumbItem, classNames?.item)}
            >
              {current ? (
                <span
                  aria-current="page"
                  className={cn(theme.slots.breadcrumbCurrent, classNames?.current)}
                >
                  {item.label}
                </span>
              ) : item.href ? (
                <a
                  href={item.href}
                  aria-disabled={item.disabled || undefined}
                  className={cn(theme.slots.breadcrumbLink, classNames?.link)}
                  onClick={(event) => handleClick(event, item, index)}
                >
                  {item.label}
                </a>
              ) : (
                <button
                  type="button"
                  disabled={item.disabled}
                  className={cn(
                    theme.slots.breadcrumbLink,
                    'bg-transparent p-0',
                    classNames?.link,
                  )}
                  onClick={(event) => handleClick(event, item, index)}
                >
                  {item.label}
                </button>
              )}
              {index < items.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    theme.slots.breadcrumbSeparator,
                    classNames?.separator,
                  )}
                >
                  {separator}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
